import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import ResponsiveNavigation from '../../components/ResponsiveNavigation';
import {
  ArrowLeft, Building2, Users, FileText, Activity, Pencil, MailPlus,
  CheckCircle2, BriefcaseBusiness, Send, FileSignature, XOctagon, LogIn, UserPlus, PlayCircle,
  StickyNote, Paperclip, Receipt, Plus, Trash2, Download, Save, X, ExternalLink,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const TABS = [
  { id: 'members',     label: 'Members',     icon: Users },
  { id: 'roles',       label: 'Roles',       icon: BriefcaseBusiness },
  { id: 'activities',  label: 'Activities',  icon: Activity },
  { id: 'notes',       label: 'Notes',       icon: StickyNote },
  { id: 'attachments', label: 'Attachments', icon: Paperclip },
  { id: 'licenses',    label: 'Billing & Licences',    icon: Receipt },
];

const ROLE_LABEL = {
  client_admin: 'Admin Recruiter',
  CADM: 'Admin Recruiter',
  RADM: 'Admin Recruiter',
  client_recruiter: 'Recruiter',
  CREC: 'Recruiter',
  RECR: 'Recruiter',
  jobasti_admin: 'Super Admin',
  JADM: 'Super Admin',
};

const ROLE_BADGE = {
  client_admin: 'bg-purple-100 text-purple-700',
  CADM:         'bg-purple-100 text-purple-700',
  RADM:         'bg-purple-100 text-purple-700',
  client_recruiter: 'bg-blue-100 text-blue-700',
  CREC:             'bg-blue-100 text-blue-700',
  RECR:             'bg-blue-100 text-blue-700',
};

const ACTION_META = {
  position_created:     { icon: FileText,       color: 'bg-blue-100 text-blue-600',     label: 'created a position' },
  position_published:   { icon: CheckCircle2,   color: 'bg-green-100 text-green-600',   label: 'published a position' },
  position_closed:      { icon: XOctagon,       color: 'bg-gray-100 text-gray-600',     label: 'closed a position' },
  video_invite_sent:    { icon: PlayCircle,     color: 'bg-indigo-100 text-indigo-600', label: 'sent a video interview invite' },
  async_invite_sent:    { icon: Send,           color: 'bg-cyan-100 text-cyan-600',     label: 'sent an async video invite' },
  login_success:        { icon: LogIn,          color: 'bg-emerald-50 text-emerald-600',label: 'signed in' },
  contract_email_sent:  { icon: FileSignature,  color: 'bg-amber-100 text-amber-600',   label: 'sent a contract email' },
  rejection_email_sent: { icon: XOctagon,       color: 'bg-rose-100 text-rose-600',     label: 'sent a rejection email' },
  recruiter_invited:    { icon: UserPlus,       color: 'bg-blue-100 text-blue-600',     label: 'invited a recruiter' },
  recruiter_joined:     { icon: UserPlus,       color: 'bg-green-100 text-green-600',   label: 'joined the company' },
};

const formatRelative = (iso) => {
  const d = new Date(iso);
  const diffSec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diffSec < 60) return 'just now';
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  if (diffSec < 604800) return `${Math.floor(diffSec / 86400)}d ago`;
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const AdminCompanyDetail = () => {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [company, setCompany] = useState(null);
  const [activeTab, setActiveTab] = useState('members');
  const [loading, setLoading] = useState(true);

  const backendBase = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

  const fetchDetail = useCallback(async () => {
    try {
      setLoading(true);
      const r = await axios.get(`${backendBase}/api/admin/companies/${companyId}/detail`);
      setCompany(r.data.data);
    } catch (e) {
      console.error('company detail load error', e);
    } finally {
      setLoading(false);
    }
  }, [backendBase, companyId]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  if (loading && !company) {
    return (
      <div className="min-h-screen bg-gray-50">
        <ResponsiveNavigation />
        <div className="p-8 text-center text-gray-500">Loading…</div>
      </div>
    );
  }

  if (!company) {
    return (
      <div className="min-h-screen bg-gray-50">
        <ResponsiveNavigation />
        <div className="p-8 text-center text-gray-600" data-testid="company-not-found">
          Company not found.
          <button onClick={() => navigate('/admin/companies')} className="ml-3 text-blue-600 hover:underline">
            Back to companies
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-company-detail-page">
      <ResponsiveNavigation />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Back arrow + breadcrumb */}
        <button
          onClick={() => navigate('/admin/companies')}
          className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 mb-4"
          data-testid="company-detail-back-btn"
        >
          <ArrowLeft className="w-4 h-4" /> Companies
        </button>

        {/* Company header */}
        <div className="bg-white border border-gray-200 rounded-2xl p-6 mb-6 flex items-start gap-5" data-testid="company-detail-header">
          <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center flex-shrink-0">
            <Building2 className="w-7 h-7 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 truncate">{company.name}</h1>
                <p className="text-sm text-gray-500 mt-0.5">
                  {[company.city, company.country, company.industry].filter(Boolean).join(' · ')}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => navigate(`/admin/companies/${companyId}/edit`)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:border-gray-300 hover:bg-gray-50"
                  data-testid="company-edit-btn"
                  title="Edit company information"
                >
                  <Pencil className="w-3.5 h-3.5" /> Edit
                </button>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-1.5 mt-4 text-xs">
              <Stat label="Open positions" value={company.openPositions} />
              <Stat label="Recruiters" value={`${company.licenseUsed} / ${company.licenseTotal}`} />
              <Stat label="Available licenses" value={company.licenseAvailable} highlight={company.licenseAvailable > 0} />
              <Stat label="Joined" value={company.created_at ? new Date(company.created_at).toLocaleDateString('en-GB') : '—'} />
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
          <div className="flex border-b border-gray-100" data-testid="company-detail-tabs">
            {TABS.map((t) => {
              const Icon = t.icon;
              const active = activeTab === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => setActiveTab(t.id)}
                  className={`flex items-center gap-2 px-5 py-3.5 text-sm font-medium border-b-2 transition-colors ${
                    active
                      ? 'border-blue-600 text-blue-600 bg-blue-50/40'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                  }`}
                  data-testid={`company-tab-${t.id}`}
                  aria-current={active ? 'page' : undefined}
                >
                  <Icon className="w-4 h-4" />
                  {t.label}
                </button>
              );
            })}
          </div>

          {activeTab === 'members' && (
            <MembersTab
              companyId={companyId}
              licenseUsed={company.licenseUsed}
              licenseTotal={company.licenseTotal}
              licenseAvailable={company.licenseAvailable}
              actorUserId={userProfile?.id}
              actorName={`${userProfile?.firstName || ''} ${userProfile?.lastName || ''}`.trim()}
              onChange={fetchDetail}
            />
          )}
          {activeTab === 'roles' && <RolesTab companyId={companyId} companySlug={company.slug} />}
          {activeTab === 'activities' && <ActivitiesTab companyId={companyId} />}
          {activeTab === 'notes' && (
            <NotesTab
              companyId={companyId}
              actorUserId={userProfile?.id}
              actorName={`${userProfile?.firstName || ''} ${userProfile?.lastName || ''}`.trim()}
            />
          )}
          {activeTab === 'attachments' && (
            <AttachmentsTab
              companyId={companyId}
              actorUserId={userProfile?.id}
              actorName={`${userProfile?.firstName || ''} ${userProfile?.lastName || ''}`.trim()}
            />
          )}
          {activeTab === 'licenses' && (
            <LicensesTab companyId={companyId} onChange={fetchDetail} />
          )}
        </div>
      </div>
    </div>
  );
};

const Stat = ({ label, value, highlight }) => (
  <div className="flex items-center gap-1.5">
    <span className="text-gray-500">{label}:</span>
    <span className={`font-semibold ${highlight ? 'text-green-600' : 'text-gray-900'}`}>{value}</span>
  </div>
);

/* ─── Members tab ─────────────────────────────────────────────────────────── */
const MembersTab = ({ companyId, licenseUsed, licenseTotal, licenseAvailable, actorUserId, actorName, onChange }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);

  const backendBase = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${backendBase}/api/admin/companies/${companyId}/recruiters`);
      setRows(r.data.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [backendBase, companyId]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-6" data-testid="company-members-tab">
      <div className="flex items-center justify-between mb-5">
        <div>
          <p className="text-sm text-gray-600">
            <span className="font-semibold text-gray-900">{licenseUsed}</span> of <span className="font-semibold text-gray-900">{licenseTotal}</span> licenses used
            {' · '}
            <span className={licenseAvailable > 0 ? 'text-green-600 font-semibold' : 'text-red-600 font-semibold'}>
              {licenseAvailable} available
            </span>
          </p>
        </div>
        <button
          onClick={() => setShowInvite(true)}
          disabled={licenseAvailable <= 0}
          className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed rounded-lg"
          data-testid="invite-member-btn"
        >
          <MailPlus className="w-4 h-4" /> Invite member
        </button>
      </div>

      {loading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-gray-500 text-center py-8" data-testid="members-empty">No members yet.</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
              <tr>
                <th className="text-left px-4 py-2.5 font-semibold">Name</th>
                <th className="text-left px-4 py-2.5 font-semibold">Role</th>
                <th className="text-left px-4 py-2.5 font-semibold">Email</th>
                <th className="text-left px-4 py-2.5 font-semibold">Status</th>
                <th className="text-left px-4 py-2.5 font-semibold">Last login</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((m) => (
                <tr key={m.id} className="hover:bg-gray-50" data-testid={`member-row-${m.id}`}>
                  <td className="px-4 py-3 font-medium text-gray-900">{m.first_name} {m.last_name}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${ROLE_BADGE[m.role] || 'bg-gray-100 text-gray-600'}`}>
                        {ROLE_LABEL[m.role] || m.role}
                      </span>
                      {m.license_exempt && (
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-gray-100 text-gray-600" title="Does not consume a license">
                          Exempt
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-600">{m.email}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${
                      (m.status || 'active') === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                    }`}>{m.status || 'active'}</span>
                  </td>
                  <td className="px-4 py-3 text-gray-500">
                    {m.last_login_at ? formatRelative(m.last_login_at) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showInvite && (
        <InviteMemberModal
          companyId={companyId}
          actorUserId={actorUserId}
          actorName={actorName}
          onClose={() => setShowInvite(false)}
          onSent={() => {
            setShowInvite(false);
            load();
            onChange?.();
          }}
        />
      )}
    </div>
  );
};

const InviteMemberModal = ({ companyId, actorUserId, actorName, onClose, onSent }) => {
  const [form, setForm] = useState({ email: '', firstName: '', lastName: '', role: 'client_recruiter', licenseExempt: false });
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  const backendBase = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

  const send = async () => {
    setError('');
    if (!form.email) { setError('Email is required'); return; }
    setSending(true);
    try {
      await axios.post(`${backendBase}/api/admin/companies/${companyId}/invite`, {
        ...form,
        actorUserId,
        actorName,
      });
      onSent();
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to send invitation');
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="invite-modal-overlay"
    >
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
        data-testid="invite-modal"
      >
        <div className="px-6 py-4 border-b border-gray-100">
          <h2 className="text-lg font-bold text-gray-900">Invite a new member</h2>
          <p className="text-xs text-gray-500 mt-0.5">An email with a registration link will be sent.</p>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <FieldInput label="First name" value={form.firstName} onChange={(v) => setForm({ ...form, firstName: v })} testId="invite-firstname" />
            <FieldInput label="Last name"  value={form.lastName}  onChange={(v) => setForm({ ...form, lastName:  v })} testId="invite-lastname" />
          </div>
          <FieldInput label="Email" type="email" required value={form.email} onChange={(v) => setForm({ ...form, email: v })} testId="invite-email" />
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Role</label>
            <select
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
              className="w-full text-sm border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500"
              data-testid="invite-role"
            >
              <option value="client_recruiter">Recruiter</option>
              <option value="client_admin">Admin Recruiter</option>
            </select>
          </div>
          <label className="flex items-start gap-2.5 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={form.licenseExempt}
              onChange={(e) => setForm({ ...form, licenseExempt: e.target.checked })}
              className="mt-0.5 w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
              data-testid="invite-license-exempt"
            />
            <span>
              <span className="block text-xs font-semibold text-gray-800">Doesn&apos;t count toward licenses</span>
              <span className="block text-[11px] text-gray-500 leading-snug mt-0.5">Use for observers, contractors, or members who shouldn&apos;t consume a paid license.</span>
            </span>
          </label>
          {error && <p className="text-xs text-red-600" data-testid="invite-error">{error}</p>}
        </div>
        <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
            data-testid="invite-cancel"
          >
            Cancel
          </button>
          <button
            onClick={send}
            disabled={sending}
            className="px-5 py-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 rounded-lg"
            data-testid="invite-send"
          >
            {sending ? 'Sending…' : 'Send invitation'}
          </button>
        </div>
      </div>
    </div>
  );
};

const FieldInput = ({ label, value, onChange, type = 'text', required, testId }) => (
  <div>
    <label className="block text-xs font-semibold text-gray-700 mb-1.5">
      {label}{required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full text-sm border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500"
      data-testid={testId}
    />
  </div>
);

/* ─── Roles tab ───────────────────────────────────────────────────────────── */
const RolesTab = ({ companyId, companySlug }) => {
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);

  const backendBase = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${backendBase}/api/admin/companies/${companyId}/positions`);
        setPositions(r.data.data || []);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, [backendBase, companyId]);

  const openPublicView = (position) => {
    if (position.status !== 'published') return;
    if (!companySlug) {
      alert('This company has no slug configured yet — public view is unavailable.');
      return;
    }
    const url = `${window.location.origin}/${companySlug}/jobs/${position.id}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  if (loading) return <p className="p-6 text-sm text-gray-500">Loading…</p>;

  return (
    <div className="p-6" data-testid="company-roles-tab">
      {positions.length === 0 ? (
        <p className="text-sm text-gray-500 text-center py-8" data-testid="roles-empty">No roles yet.</p>
      ) : (
        <div className="space-y-3">
          {positions.map((p) => {
            const isPublished = p.status === 'published';
            return (
              <div
                key={p.id}
                role={isPublished ? 'button' : undefined}
                tabIndex={isPublished ? 0 : undefined}
                onClick={() => openPublicView(p)}
                onKeyDown={(e) => {
                  if (isPublished && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault();
                    openPublicView(p);
                  }
                }}
                className={`border border-gray-200 rounded-lg p-4 transition-all ${
                  isPublished
                    ? 'cursor-pointer hover:shadow-md hover:border-blue-300'
                    : 'opacity-90 hover:shadow-sm'
                }`}
                data-testid={`role-row-${p.id}`}
                title={isPublished ? 'Open the public job page in a new tab' : `Status: ${p.status} — public view not available`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <h3 className="font-semibold text-gray-900">
                      {p.title}
                      {isPublished && (
                        <ExternalLink className="inline-block w-3.5 h-3.5 text-blue-500 ml-1.5 -mt-0.5" />
                      )}
                    </h3>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {[p.location, p.employment_type, p.workplace].filter(Boolean).join(' · ')}
                    </p>
                    {(p.salary_min || p.salary_max) && (
                      <p className="text-xs text-gray-500 mt-0.5">
                        {[p.salary_min, p.salary_max].filter(Boolean).join(' – ')} {p.salary_currency || ''}
                      </p>
                    )}
                  </div>
                  <span
                    className={`px-2 py-0.5 rounded-full text-[11px] font-semibold whitespace-nowrap ${
                      isPublished
                        ? 'bg-green-100 text-green-700'
                        : p.status === 'draft'
                        ? 'bg-amber-100 text-amber-700'
                        : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {p.status || 'draft'}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

/* ─── Activities tab ──────────────────────────────────────────────────────── */
const ACTIVITY_GROUPS = [
  { id: 'all',          label: 'All',           actions: null },
  { id: 'signins',      label: 'Sign-ins',      actions: ['login_success', 'login_failed', 'logout'] },
  { id: 'roles',        label: 'Roles',         actions: ['position_created', 'position_published', 'position_closed'] },
  { id: 'applications', label: 'Applications',  actions: [] }, // rendered separately
  { id: 'contracts',    label: 'Contracts',     actions: ['contract_email_sent', 'rejection_email_sent'] },
  { id: 'interviews',   label: 'Interviews',    actions: ['video_invite_sent', 'async_invite_sent'] },
  { id: 'tags',         label: 'Jobasti Tags',  actions: [] }, // rendered separately
];

const scoreBadgeClass = (score) => {
  if (score == null) return 'bg-gray-100 text-gray-500';
  if (score >= 85) return 'bg-green-100 text-green-700';
  if (score >= 70) return 'bg-blue-100 text-blue-700';
  if (score >= 50) return 'bg-amber-100 text-amber-700';
  return 'bg-rose-100 text-rose-700';
};

const ActivitiesTab = ({ companyId }) => {
  const [items, setItems] = useState([]);
  const [tags, setTags] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tagsLoading, setTagsLoading] = useState(false);
  const [appsLoading, setAppsLoading] = useState(false);
  const [nextBefore, setNextBefore] = useState(null);
  const [group, setGroup] = useState('all');

  const backendBase = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

  const load = useCallback(async (before = null) => {
    try {
      const url = `${backendBase}/api/admin/companies/${companyId}/activities?limit=50${before ? `&before=${encodeURIComponent(before)}` : ''}`;
      const r = await axios.get(url);
      const data = r.data.data || [];
      setItems((prev) => (before ? [...prev, ...data] : data));
      setNextBefore(r.data.nextBefore || null);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [backendBase, companyId]);

  useEffect(() => { load(); }, [load]);

  // Load Jobasti Tags on demand
  useEffect(() => {
    if (group !== 'tags') return;
    let cancelled = false;
    (async () => {
      setTagsLoading(true);
      try {
        const r = await axios.get(`${backendBase}/api/admin/companies/${companyId}/tags`);
        if (!cancelled) setTags(r.data.data || []);
      } catch (e) {
        console.error('load tags failed', e);
      } finally {
        if (!cancelled) setTagsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [group, backendBase, companyId]);

  // Load Applications on demand
  useEffect(() => {
    if (group !== 'applications') return;
    let cancelled = false;
    (async () => {
      setAppsLoading(true);
      try {
        const r = await axios.get(`${backendBase}/api/admin/companies/${companyId}/applications`);
        if (!cancelled) setApplications(r.data.data || []);
      } catch (e) {
        console.error('load applications failed', e);
      } finally {
        if (!cancelled) setAppsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [group, backendBase, companyId]);

  const activeGroup = ACTIVITY_GROUPS.find((g) => g.id === group) || ACTIVITY_GROUPS[0];
  const filtered = activeGroup.actions === null
    ? items
    : items.filter((it) => activeGroup.actions.includes(it.action));

  if (loading) return <p className="p-6 text-sm text-gray-500">Loading…</p>;

  return (
    <div className="p-6" data-testid="company-activities-tab">
      {/* Sub-tabs */}
      <div className="flex flex-wrap gap-2 mb-5 border-b border-gray-100 pb-3" data-testid="activities-subtabs">
        {ACTIVITY_GROUPS.map((g) => (
          <button
            key={g.id}
            onClick={() => setGroup(g.id)}
            className={`px-3 py-1.5 text-sm rounded-full transition-colors ${
              group === g.id
                ? 'bg-blue-600 text-white font-medium'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
            data-testid={`activities-subtab-${g.id}`}
          >
            {g.label}
          </button>
        ))}
      </div>

      {group === 'tags' ? (
        tagsLoading ? (
          <p className="text-sm text-gray-500">Loading tags…</p>
        ) : tags.length === 0 ? (
          <div className="text-center py-10" data-testid="tags-empty">
            <Activity className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-sm font-medium text-gray-700">No tags yet</p>
            <p className="text-xs text-gray-500 mt-1">
              Any Jobasti admin who @-mentions this company in a TODO will show up here.
            </p>
          </div>
        ) : (
          <ul className="relative" data-testid="tags-list">
            <div className="absolute left-[15px] top-2 bottom-2 w-px bg-gray-200" />
            {tags.map((t) => (
              <li key={t.id} className="relative pl-12 py-3" data-testid={`tag-${t.id}`}>
                <div className="absolute left-0 top-3 w-8 h-8 rounded-full flex items-center justify-center bg-blue-100 text-blue-600">
                  <StickyNote className="w-4 h-4" />
                </div>
                <p className="text-sm text-gray-900">
                  <span className="font-semibold">{t.actor_name || 'A Jobasti admin'}</span>
                  <span className="text-gray-600"> tagged this company</span>
                </p>
                <p className="text-sm text-gray-800 mt-1 italic">&ldquo;{t.todo_text}&rdquo;</p>
                <p className="text-xs text-gray-400 mt-0.5">{formatRelative(t.created_at)}</p>
              </li>
            ))}
          </ul>
        )
      ) : group === 'applications' ? (
        appsLoading ? (
          <p className="text-sm text-gray-500">Loading applications…</p>
        ) : applications.length === 0 ? (
          <div className="text-center py-10" data-testid="applications-empty">
            <Send className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-sm font-medium text-gray-700">No applications yet</p>
            <p className="text-xs text-gray-500 mt-1">
              New applicants will appear here with their AI match score.
            </p>
          </div>
        ) : (
          <ul className="relative" data-testid="applications-list">
            <div className="absolute left-[15px] top-2 bottom-2 w-px bg-gray-200" />
            {applications.map((a) => {
              const name = [a.first_name, a.last_name].filter(Boolean).join(' ') || a.email || 'Applicant';
              const score = typeof a.match_score === 'number' ? Math.round(a.match_score) : null;
              return (
                <li key={a.id} className="relative pl-12 py-3" data-testid={`application-${a.id}`}>
                  <div className="absolute left-0 top-3 w-8 h-8 rounded-full flex items-center justify-center bg-emerald-100 text-emerald-600">
                    <Send className="w-4 h-4" />
                  </div>
                  <p className="text-sm text-gray-900">
                    <span className="font-semibold">{name}</span>
                    <span className="text-gray-600"> applied for </span>
                    <span className="text-gray-900 font-medium">&ldquo;{a.positions?.title || 'a role'}&rdquo;</span>
                  </p>
                  <div className="flex items-center gap-2 mt-1">
                    <span
                      className={`inline-flex items-center text-[11px] font-semibold px-2 py-0.5 rounded-full ${scoreBadgeClass(score)}`}
                      data-testid={`application-score-${a.id}`}
                    >
                      {score != null ? `${score}% match` : 'No match score'}
                    </span>
                    <span className="text-xs text-gray-400">{formatRelative(a.applied_at)}</span>
                  </div>
                </li>
              );
            })}
          </ul>
        )
      ) : filtered.length === 0 ? (
        <div className="text-center py-10" data-testid="activities-empty">
          <Activity className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-sm font-medium text-gray-700">
            {group === 'all' ? 'No activity yet' : `No ${activeGroup.label.toLowerCase()} yet`}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            Events will appear here as the company creates positions, sends invites, or signs in.
          </p>
        </div>
      ) : (
        <ul className="relative">
          <div className="absolute left-[15px] top-2 bottom-2 w-px bg-gray-200" />
          {filtered.map((it) => {
            const meta = ACTION_META[it.action] || { icon: Activity, color: 'bg-gray-100 text-gray-600', label: it.action.replace(/_/g, ' ') };
            const Icon = meta.icon;
            return (
              <li key={it.id} className="relative pl-12 py-3" data-testid={`activity-${it.id}`}>
                <div className={`absolute left-0 top-3 w-8 h-8 rounded-full flex items-center justify-center ${meta.color}`}>
                  <Icon className="w-4 h-4" />
                </div>
                <p className="text-sm text-gray-900">
                  <span className="font-semibold">{it.actor_name || 'Someone'}</span>
                  {' '}
                  <span className="text-gray-600">{meta.label}</span>
                  {it.target_label && (
                    <>
                      {' '}
                      <span className="text-gray-900 font-medium">&ldquo;{it.target_label}&rdquo;</span>
                    </>
                  )}
                </p>
                <p className="text-xs text-gray-400 mt-0.5">{formatRelative(it.created_at)}</p>
              </li>
            );
          })}
          {nextBefore && group === 'all' && (
            <li className="pt-3 pl-12">
              <button
                onClick={() => load(nextBefore)}
                className="text-xs font-medium text-blue-600 hover:text-blue-700"
                data-testid="activities-load-more"
              >
                Load more
              </button>
            </li>
          )}
        </ul>
      )}
    </div>
  );
};

/* ─── Notes tab ───────────────────────────────────────────────────────────── */
const NotesTab = ({ companyId, actorUserId, actorName }) => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editingBody, setEditingBody] = useState('');
  const [saving, setSaving] = useState(false);

  const backendBase = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${backendBase}/api/admin/companies/${companyId}/notes`);
      setNotes(r.data.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [backendBase, companyId]);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!draft.trim()) return;
    setSaving(true);
    try {
      await axios.post(`${backendBase}/api/admin/companies/${companyId}/notes`, {
        body: draft, authorUserId: actorUserId, authorName: actorName,
      });
      setDraft('');
      await load();
    } catch (e) { alert(e?.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  const saveEdit = async (id) => {
    if (!editingBody.trim()) return;
    try {
      await axios.put(`${backendBase}/api/admin/companies/${companyId}/notes/${id}`, { body: editingBody });
      setEditingId(null);
      setEditingBody('');
      await load();
    } catch (e) { alert(e?.response?.data?.message || 'Failed'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this note? This cannot be undone.')) return;
    try {
      await axios.delete(`${backendBase}/api/admin/companies/${companyId}/notes/${id}`);
      await load();
    } catch (e) { alert('Failed'); }
  };

  return (
    <div className="p-6" data-testid="company-notes-tab">
      <div className="mb-5">
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Add an internal note about this company. These notes are only visible to JobastiIQ staff."
          rows={3}
          className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500 resize-vertical"
          data-testid="note-draft-input"
        />
        <div className="flex justify-end mt-2">
          <button
            onClick={create}
            disabled={saving || !draft.trim()}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 rounded-lg"
            data-testid="note-save-btn"
          >
            <Plus className="w-3.5 h-3.5" /> Add note
          </button>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : notes.length === 0 ? (
        <div className="text-center py-10" data-testid="notes-empty">
          <StickyNote className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-sm font-medium text-gray-700">No notes yet</p>
          <p className="text-xs text-gray-500 mt-1">Internal-only notes about this company will appear here.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {notes.map((n) => (
            <div key={n.id} className="border border-gray-200 rounded-lg p-4 bg-amber-50/30" data-testid={`note-${n.id}`}>
              {editingId === n.id ? (
                <>
                  <textarea
                    value={editingBody}
                    onChange={(e) => setEditingBody(e.target.value)}
                    rows={3}
                    className="w-full text-sm border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    data-testid={`note-edit-input-${n.id}`}
                  />
                  <div className="flex items-center justify-end gap-2 mt-2">
                    <button onClick={() => { setEditingId(null); setEditingBody(''); }} className="px-3 py-1.5 text-xs text-gray-600 hover:text-gray-800">Cancel</button>
                    <button onClick={() => saveEdit(n.id)} className="px-3 py-1.5 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded">Save</button>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-sm text-gray-800 whitespace-pre-wrap">{n.body}</p>
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-amber-100/60">
                    <p className="text-[11px] text-gray-500">
                      {n.author_name || 'Unknown'} · {formatRelative(n.created_at)}
                      {n.updated_at && n.updated_at !== n.created_at && ' (edited)'}
                    </p>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => { setEditingId(n.id); setEditingBody(n.body); }}
                        className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                        title="Edit"
                        data-testid={`note-edit-${n.id}`}
                      ><Pencil className="w-3.5 h-3.5" /></button>
                      <button
                        onClick={() => remove(n.id)}
                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                        title="Delete"
                        data-testid={`note-delete-${n.id}`}
                      ><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ─── Attachments tab ─────────────────────────────────────────────────────── */
const formatBytes = (b) => {
  if (!b) return '—';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.min(sizes.length - 1, Math.floor(Math.log(b) / Math.log(k)));
  return `${(b / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
};

const AttachmentsTab = ({ companyId, actorUserId, actorName }) => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = React.useRef(null);
  const backendBase = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${backendBase}/api/admin/companies/${companyId}/attachments`);
      setFiles(r.data.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [backendBase, companyId]);

  useEffect(() => { load(); }, [load]);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      if (actorUserId) fd.append('uploadedByUserId', actorUserId);
      if (actorName) fd.append('uploadedByName', actorName);
      await axios.post(`${backendBase}/api/admin/companies/${companyId}/attachments`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await load();
    } catch (e) { alert(e?.response?.data?.message || 'Upload failed'); }
    finally { setUploading(false); }
  };

  const download = async (id) => {
    try {
      const r = await axios.get(`${backendBase}/api/admin/companies/${companyId}/attachments/${id}/url`);
      if (r.data?.data?.url) window.open(r.data.data.url, '_blank', 'noopener');
    } catch (e) { alert('Failed to fetch link'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this file? This cannot be undone.')) return;
    try {
      await axios.delete(`${backendBase}/api/admin/companies/${companyId}/attachments/${id}`);
      await load();
    } catch (e) { alert('Failed'); }
  };

  return (
    <div className="p-6" data-testid="company-attachments-tab">
      <div className="flex items-center justify-between mb-5">
        <p className="text-sm text-gray-600">
          Internal documents (contracts, NDAs, signed agreements). Only JobastiIQ staff can see these.
        </p>
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          onChange={(e) => upload(e.target.files?.[0])}
          data-testid="attachment-file-input"
        />
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 rounded-lg"
          data-testid="attachment-upload-btn"
        >
          <Plus className="w-3.5 h-3.5" /> {uploading ? 'Uploading…' : 'Upload file'}
        </button>
      </div>

      {loading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : files.length === 0 ? (
        <div className="text-center py-10" data-testid="attachments-empty">
          <Paperclip className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-sm font-medium text-gray-700">No files yet</p>
          <p className="text-xs text-gray-500 mt-1">Drop or upload contracts and other documents here.</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
              <tr>
                <th className="text-left px-4 py-2.5 font-semibold">File</th>
                <th className="text-left px-4 py-2.5 font-semibold">Size</th>
                <th className="text-left px-4 py-2.5 font-semibold">Uploaded by</th>
                <th className="text-left px-4 py-2.5 font-semibold">When</th>
                <th className="text-right px-4 py-2.5 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {files.map((f) => (
                <tr key={f.id} className="hover:bg-gray-50" data-testid={`attachment-row-${f.id}`}>
                  <td className="px-4 py-3 font-medium text-gray-900 break-all">{f.file_name}</td>
                  <td className="px-4 py-3 text-gray-500">{formatBytes(f.size_bytes)}</td>
                  <td className="px-4 py-3 text-gray-500">{f.uploaded_by_name || '—'}</td>
                  <td className="px-4 py-3 text-gray-500">{formatRelative(f.uploaded_at)}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-1">
                      <button onClick={() => download(f.id)} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded" title="Download" data-testid={`attachment-download-${f.id}`}>
                        <Download className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => remove(f.id)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded" title="Delete" data-testid={`attachment-delete-${f.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

/* ─── Licenses tab ────────────────────────────────────────────────────────── */
const formatLicenseDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch { return iso; }
};

const formatMoney = (cents, currency = 'EUR') => {
  if (!cents || cents === 0) return '—';
  try {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(cents / 100);
  } catch {
    return `${(cents / 100).toFixed(2)} ${currency}`;
  }
};

const CURRENCY_OPTIONS = ['EUR', 'USD', 'GBP', 'CHF'];

const LicensesTab = ({ companyId, onChange }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ total: '10', totalAmount: '', currency: 'EUR', expirationDate: '', paymentDate: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingGrant, setEditingGrant] = useState(null);

  const backendBase = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${backendBase}/api/admin/companies/${companyId}/licenses`);
      setData(r.data.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [backendBase, companyId]);

  useEffect(() => { load(); }, [load]);

  const openEdit = () => {
    setForm({
      total: String(data?.base ?? 10),
      totalAmount: data?.baseTotalAmountCents ? String(data.baseTotalAmountCents / 100) : '',
      currency: data?.currency || 'EUR',
      expirationDate: data?.expirationDate || '',
      paymentDate: data?.paymentDate || '',
    });
    setError('');
    setEditing(true);
  };

  const save = async () => {
    setError('');
    const total = parseInt(form.total, 10);
    if (isNaN(total) || total < 0) { setError('Number of licenses must be a non-negative integer.'); return; }
    setSaving(true);
    try {
      await axios.put(`${backendBase}/api/admin/companies/${companyId}/licenses/base`, {
        total,
        totalAmount: form.totalAmount === '' ? 0 : Number(form.totalAmount),
        currency: form.currency,
        expirationDate: form.expirationDate || null,
        paymentDate: form.paymentDate || null,
      });
      setEditing(false);
      await load();
      onChange?.();
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to save');
    } finally { setSaving(false); }
  };

  const removeGrant = async (grantId) => {
    if (!window.confirm('Remove this license batch? Members will not be deleted but the total seat cap will decrease.')) return;
    try {
      await axios.delete(`${backendBase}/api/admin/companies/${companyId}/licenses/grant/${grantId}`);
      await load();
      onChange?.();
    } catch (e) { alert(e?.response?.data?.message || 'Failed'); }
  };

  if (loading || !data) return <p className="p-6 text-sm text-gray-500">Loading…</p>;

  return (
    <div className="p-6" data-testid="company-licenses-tab">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900">Licenses</h2>
        {!editing && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowAddModal(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg"
              data-testid="license-add-btn"
            >
              <Plus className="w-3.5 h-3.5" /> Add licenses
            </button>
            <button
              onClick={openEdit}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:border-gray-300 hover:bg-gray-50"
              data-testid="license-edit-btn"
            >
              <Pencil className="w-3.5 h-3.5" /> Edit
            </button>
          </div>
        )}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="licenses-table">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="text-left py-3 px-4 font-medium text-gray-700">Number of Licenses</th>
              <th className="text-left py-3 px-4 font-medium text-gray-700">Active Licenses</th>
              <th className="text-left py-3 px-4 font-medium text-gray-700">Amount</th>
              <th className="text-left py-3 px-4 font-medium text-gray-700">Expiration Date</th>
              <th className="text-left py-3 px-4 font-medium text-gray-700">Payment Date</th>
              {(data.grants?.length > 0) && <th className="text-right py-3 px-4 font-medium text-gray-700"></th>}
            </tr>
          </thead>
          <tbody>
            {/* Base row */}
            <tr className="border-b border-gray-100">
              {editing ? (
                <>
                  <td className="py-3 px-4">
                    <input
                      type="number" min={0} value={form.total}
                      onChange={(e) => setForm({ ...form, total: e.target.value })}
                      className="w-20 text-sm border border-gray-300 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      data-testid="license-total-input"
                    />
                  </td>
                  <td className="py-3 px-4 text-gray-800">{data.used}</td>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-1.5">
                      <input
                        type="number" min={0} step={0.01} value={form.totalAmount}
                        onChange={(e) => setForm({ ...form, totalAmount: e.target.value })}
                        placeholder="0.00"
                        className="w-28 text-sm border border-gray-300 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        data-testid="license-unit-amount-input"
                      />
                      <select
                        value={form.currency}
                        onChange={(e) => setForm({ ...form, currency: e.target.value })}
                        className="text-sm border border-gray-300 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white"
                        data-testid="license-currency-select"
                      >
                        {CURRENCY_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                      <span className="text-[11px] text-gray-500">total</span>
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <input
                      type="date" value={form.expirationDate || ''}
                      onChange={(e) => setForm({ ...form, expirationDate: e.target.value })}
                      className="text-sm border border-gray-300 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      data-testid="license-expiration-input"
                    />
                  </td>
                  <td className="py-3 px-4">
                    <input
                      type="date" value={form.paymentDate || ''}
                      onChange={(e) => setForm({ ...form, paymentDate: e.target.value })}
                      className="text-sm border border-gray-300 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      data-testid="license-payment-input"
                    />
                  </td>
                  {data.grants?.length > 0 && <td></td>}
                </>
              ) : (
                <>
                  <td className="py-3 px-4 text-gray-800 font-semibold" data-testid="license-total-value">{data.base}</td>
                  <td className="py-3 px-4 text-gray-800" data-testid="license-used-value">{data.used}</td>
                  <td className="py-3 px-4 text-gray-700" data-testid="license-amount-value">
                    {data.baseTotalAmountCents > 0 ? (
                      <span>
                        {formatMoney(data.baseTotalAmountCents, data.currency)}
                        <span className="text-[11px] text-gray-500 ml-1">total</span>
                      </span>
                    ) : '—'}
                  </td>
                  <td className="py-3 px-4 text-gray-600" data-testid="license-expiration-value">{formatLicenseDate(data.expirationDate)}</td>
                  <td className="py-3 px-4 text-gray-600" data-testid="license-payment-value">{formatLicenseDate(data.paymentDate)}</td>
                  {data.grants?.length > 0 && <td></td>}
                </>
              )}
            </tr>

            {/* Grant rows (always visible — read-only during base-row edit) */}
            {(data.grants || []).map((g) => (
              <tr key={g.id} className="border-b border-gray-100 last:border-0" data-testid={`grant-row-${g.id}`}>
                <td className="py-3 px-4 text-gray-800 font-semibold">{g.seats}</td>
                <td className="py-3 px-4 text-gray-400">—</td>
                <td className="py-3 px-4 text-gray-700">
                  {g.totalAmountCents > 0 ? (
                    <span>
                      {formatMoney(g.totalAmountCents, data.currency)}
                      <span className="text-[11px] text-gray-500 ml-1">total</span>
                    </span>
                  ) : '—'}
                </td>
                <td className="py-3 px-4 text-gray-600">{formatLicenseDate(g.expirationDate)}</td>
                <td className="py-3 px-4 text-gray-600">{formatLicenseDate(g.paymentDate)}</td>
                <td className="py-3 px-4 text-right">
                  {!editing && (
                    <div className="inline-flex items-center gap-1">
                      <button
                        onClick={() => setEditingGrant(g)}
                        className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                        title="Edit batch"
                        data-testid={`grant-edit-${g.id}`}
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => removeGrant(g.id)}
                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                        title="Remove batch"
                        data-testid={`grant-delete-${g.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="mt-4 flex items-center justify-end gap-3">
          {error && <p className="text-xs text-red-600 mr-auto" data-testid="license-error">{error}</p>}
          <button onClick={() => setEditing(false)} className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900" data-testid="license-cancel">Cancel</button>
          <button onClick={save} disabled={saving} className="inline-flex items-center gap-1.5 px-5 py-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 rounded-lg" data-testid="license-save">
            <Save className="w-3.5 h-3.5" /> {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      )}

      <p className="text-xs text-gray-500 mt-6">
        Default is 10 licenses per company. Edit the base row to set price, currency, and contract dates. Use <span className="font-semibold">Add licenses</span> to add extra batches at a customised deal price — click the pencil next to a batch to edit it later.
      </p>

      {showAddModal && (
        <AddLicensesModal
          companyId={companyId}
          currency={data.currency}
          onClose={() => setShowAddModal(false)}
          onSaved={() => {
            setShowAddModal(false);
            load();
            onChange?.();
          }}
        />
      )}

      {editingGrant && (
        <AddLicensesModal
          companyId={companyId}
          currency={data.currency}
          grant={editingGrant}
          onClose={() => setEditingGrant(null)}
          onSaved={() => {
            setEditingGrant(null);
            load();
            onChange?.();
          }}
        />
      )}
    </div>
  );
};

const AddLicensesModal = ({ companyId, currency, grant = null, onClose, onSaved }) => {
  const isEdit = !!grant;
  const [form, setForm] = useState({
    seats: isEdit ? String(grant.seats || '') : '',
    totalAmount: isEdit && grant.totalAmountCents ? String(grant.totalAmountCents / 100) : '',
    currency: currency || 'EUR',
    expirationDate: isEdit ? (grant.expirationDate || '') : '',
    paymentDate: isEdit ? (grant.paymentDate || '') : '',
    note: isEdit ? (grant.note || '') : '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const backendBase = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

  const save = async () => {
    setError('');
    const seats = parseInt(form.seats, 10);
    if (!seats || seats <= 0) { setError('Number of licenses must be a positive integer.'); return; }
    const total = form.totalAmount === '' ? 0 : Number(form.totalAmount);
    if (isNaN(total) || total < 0) { setError('Total amount must be a non-negative number.'); return; }
    setSaving(true);
    try {
      if (isEdit) {
        await axios.patch(`${backendBase}/api/admin/companies/${companyId}/licenses/grant/${grant.id}`, {
          seats,
          totalAmount: total,
          expirationDate: form.expirationDate || null,
          paymentDate: form.paymentDate || null,
          note: form.note || null,
        });
      } else {
        await axios.post(`${backendBase}/api/admin/companies/${companyId}/licenses/grant`, {
          seats,
          totalAmount: total,
          currency: form.currency,
          expirationDate: form.expirationDate || null,
          paymentDate: form.paymentDate || null,
          note: form.note || null,
        });
      }
      onSaved();
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to save');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="add-licenses-modal-overlay">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="add-licenses-modal">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-gray-900">{isEdit ? 'Edit license batch' : 'Add licenses'}</h2>
            <p className="text-xs text-gray-500 mt-0.5">{isEdit ? 'Update the number of licenses, deal amount, or contract dates.' : 'Add an extra batch above the base. The amount is the total deal price for this batch.'}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg" data-testid="add-licenses-modal-close" aria-label="Close">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] font-semibold text-gray-700 mb-1.5">Number of licenses <span className="text-red-500">*</span></label>
              <input
                type="number" min={1} value={form.seats}
                onChange={(e) => setForm({ ...form, seats: e.target.value })}
                className="w-full text-sm border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500"
                data-testid="add-licenses-seats-input"
              />
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-gray-700 mb-1.5">Total amount for the batch</label>
              <div className="flex items-center gap-1.5">
                <input
                  type="number" min={0} step={0.01} value={form.totalAmount}
                  onChange={(e) => setForm({ ...form, totalAmount: e.target.value })}
                  placeholder="0.00"
                  className="flex-1 text-sm border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  data-testid="add-licenses-amount-input"
                />
                <select
                  value={form.currency}
                  onChange={(e) => setForm({ ...form, currency: e.target.value })}
                  disabled={isEdit}
                  className="text-sm border border-gray-300 rounded px-2 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white disabled:bg-gray-100"
                  data-testid="add-licenses-currency-select"
                >
                  {CURRENCY_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <p className="text-[10px] text-gray-500 mt-1">Combined deal price for the whole batch.</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] font-semibold text-gray-700 mb-1.5">Expiration date</label>
              <input
                type="date" value={form.expirationDate}
                onChange={(e) => setForm({ ...form, expirationDate: e.target.value })}
                className="w-full text-sm border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500"
                data-testid="add-licenses-expiration-input"
              />
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-gray-700 mb-1.5">Payment date</label>
              <input
                type="date" value={form.paymentDate}
                onChange={(e) => setForm({ ...form, paymentDate: e.target.value })}
                className="w-full text-sm border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500"
                data-testid="add-licenses-payment-input"
              />
            </div>
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-gray-700 mb-1.5">Note (optional)</label>
            <input
              type="text" value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
              placeholder="e.g. Q2 2026 expansion"
              className="w-full text-sm border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500"
              data-testid="add-licenses-note-input"
            />
          </div>
          {error && <p className="text-xs text-red-600" data-testid="add-licenses-error">{error}</p>}
        </div>
        <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900" data-testid="add-licenses-cancel">Cancel</button>
          <button
            onClick={save} disabled={saving}
            className="inline-flex items-center gap-1.5 px-5 py-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 rounded-lg"
            data-testid="add-licenses-save"
          >
            <Plus className="w-3.5 h-3.5" /> {saving ? 'Saving…' : (isEdit ? 'Save changes' : 'Add licenses')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminCompanyDetail;
